/**
 * THE HEALTH PANEL — what Scout has observed about itself, and the Owner's
 * controls over the operational issue record.
 *
 * IT IS NOT A SECOND MONITOR. The panel reads the monitor's own rows; the
 * probes, the deduplication, the two-pass resolution rule and the reopen
 * cooldown all live in `worker/scout/service/scout-health-monitor.ts`. This
 * surface can ask for a bounded check, dismiss an issue with a reason, and
 * reopen one. It cannot deploy, edit source, change configuration or make a
 * capability appear healthy.
 *
 * THE DISTINCTIONS THE OWNER ASKED FOR ARE VISIBLE, NOT COLLAPSED:
 *   — an issue's STATE (OPEN, AWAITING VERIFICATION, RESOLVED, REOPENED,
 *     DISMISSED) is separate from a notification's READ state;
 *   — a CAPABILITY's status (NOT_CONFIGURED, DEGRADED, UNAVAILABLE) is
 *     separate from the issue's lifecycle;
 *   — an automatic resolution shows its evidence, and a dismissal shows that
 *     a person made it.
 */

import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { createLoadable, expectsMembers } from '../resource';
import { readGateway, type GatewayError } from '../gateway';
import { navigate } from '../router';
import { Failure } from './failure';
import { Technical } from './about';

interface HealthEvent {
	sequence: number;
	action: string;
	fromState: string | null;
	toState: string;
	reason: string;
	evidence: Record<string, unknown>;
	at: string;
}

interface HealthIssue {
	issueId: string;
	capabilityKey: string;
	fingerprint: string;
	state: string;
	severity: string;
	title: string;
	summary: string;
	candidateId: string | null;
	notificationId: string | null;
	firstSeenAt: string;
	lastSeenAt: string;
	lastPassedAt: string | null;
	consecutivePasses: number;
	consecutiveFailures: number;
	detectedRevision: string | null;
	resolvedRevision: string | null;
	resolvedAt: string | null;
	resolutionEvidence: Record<string, unknown> | null;
	verificationRequirement: string;
}

interface HealthPayload {
	wired: boolean;
	issues: HealthIssue[];
	events: Array<{ issueId: string; events: HealthEvent[] }>;
	lastRun: {
		runId: string;
		status: string;
		startedAt: string;
		finishedAt: string | null;
		checksRun: number;
		issuesOpened: number;
		issuesResolved: number;
		detail: string | null;
	} | null;
	reason?: string;
}

function when(value: string | null): string {
	if (value === null) return 'never';
	const at = new Date(value);
	if (Number.isNaN(at.getTime())) return value;
	return new Intl.DateTimeFormat(undefined, {
		day: 'numeric',
		month: 'short',
		hour: '2-digit',
		minute: '2-digit',
	}).format(at);
}

export function HealthPanel() {
	const [includeResolved, setIncludeResolved] = useState(false);
	const health = useMemo(
		() =>
			createLoadable<HealthPayload>(
				() =>
					readGateway(
						`/api/scout/gateway/health?includeResolved=${includeResolved ? '1' : '0'}`,
					),
				expectsMembers('wired', 'issues'),
			),
		[includeResolved],
	);
	const [busy, setBusy] = useState<string | null>(null);
	const [error, setError] = useState<GatewayError | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [reason, setReason] = useState('');

	useEffect(() => {
		void health.load();
	}, [health]);

	const state = health.state.value;
	const data = state.data;

	const act = useCallback(
		async (path: string, body: Record<string, unknown>, label: string): Promise<void> => {
			setBusy(label);
			setError(null);
			setNotice(null);
			const result = await readGateway(path, { method: 'POST', body });
			setBusy(null);
			if (!result.ok) {
				setError(result.error);
				return;
			}
			setNotice(`${label} completed.`);
			await health.load();
		},
		[health],
	);

	return (
		<section class="panel" aria-labelledby="health-heading">
			<div class="record-head">
				<h2 id="health-heading">Capability health</h2>
				<span class="tag">{data?.lastRun?.status ?? 'UNKNOWN'}</span>
			</div>
			<p class="muted small">
				A bounded, deterministic check of the deployment's own capabilities. It runs on the
				existing scheduler, needs no model, and can only write Scout's own health, candidate and
				notification records.
			</p>

			<div class="filterbar" role="group" aria-label="Health controls">
				<button
					type="button"
					class="button"
					disabled={busy !== null || data?.wired === false}
					onClick={() => void act('/api/scout/gateway/health/run', {}, 'Check now')}
				>
					{busy === 'Check now' ? 'Checking…' : 'Check now'}
				</button>
				<button
					type="button"
					class={`button${includeResolved ? ' is-active' : ''}`}
					aria-pressed={includeResolved}
					onClick={() => setIncludeResolved((value) => !value)}
				>
					{includeResolved ? 'Active issues' : 'Include resolved'}
				</button>
			</div>

			{notice !== null && (
				<p class="small" role="status">
					{notice}
				</p>
			)}
			{error !== null && (
				<p class="small" role="alert">
					<strong>Not recorded.</strong> {error.message}{' '}
					<span class="muted">
						(<code>{error.name}</code>)
					</span>
				</p>
			)}

			{state.status === 'loading' && (
				<p class="notice" role="status">
					Reading the health record…
				</p>
			)}
			{state.status === 'failed' && state.error !== null && (
				<Failure error={state.error} what="the health record" onRetry={() => void health.load()} />
			)}
			{data !== null && !data.wired && (
				<p class="notice">
					<strong>The monitor is not wired in this deployment.</strong>{' '}
					{data.reason ?? 'No health checks can be run here.'}
				</p>
			)}
			{data !== null && data.wired && (
				<>
					<p class="muted small">
						Last pass {when(data.lastRun?.startedAt ?? null)}
						{data.lastRun !== null
							? ` · ${data.lastRun.checksRun} check(s) · ${data.lastRun.issuesOpened} opened · ${data.lastRun.issuesResolved} resolved`
							: ' · no pass has run yet'}
					</p>
					{data.issues.length === 0 ? (
						<p>
							<strong>No active capability issue is recorded.</strong>{' '}
							<span class="muted">
								That is the monitor's finding, not a claim that every capability is perfect: a
								capability the registry does not declare is invisible here, and a check that
								could not run is recorded as UNKNOWN rather than as healthy.
							</span>
						</p>
					) : (
						<ul class="records">
							{data.issues.map((issue) => {
								const events =
									data.events.find((entry) => entry.issueId === issue.issueId)?.events ?? [];
								const resolved = issue.state === 'RESOLVED' || issue.state === 'DISMISSED';
								return (
									<li key={issue.issueId} class={`record${resolved ? ' is-read' : ''}`}>
										<div class="record-head">
											<span class="record-title">{issue.title}</span>
											<span class={`tag${issue.severity === 'CRITICAL' ? ' tag-warn' : ''}`}>
												{issue.severity.replace(/_/g, ' ').toLowerCase()}
											</span>
											<span class={`tag${resolved ? '' : ' tag-live'}`}>
												{issue.state.replace(/_/g, ' ').toLowerCase()}
											</span>
										</div>
										<p class="record-summary">{issue.summary}</p>
										<p class="muted small">
											capability <code>{issue.capabilityKey}</code> · seen {when(issue.lastSeenAt)} ·
											first seen {when(issue.firstSeenAt)}
											{issue.resolvedAt !== null && ` · resolved ${when(issue.resolvedAt)}`}
										</p>
										<p class="small">
											<strong>How this would be verified:</strong> {issue.verificationRequirement}
										</p>
										{issue.resolutionEvidence !== null && (
											<Technical summary="Automatic resolution evidence">
												<pre class="excerpt">{JSON.stringify(issue.resolutionEvidence, null, 2)}</pre>
											</Technical>
										)}
										<p class="small">
											{issue.candidateId !== null && (
												<a
													href={`/workspace/improvements/${encodeURIComponent(issue.candidateId)}`}
													onClick={(event: MouseEvent) => {
														if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
														event.preventDefault();
														navigate({
															destination: 'workspace',
															section: 'improvements',
															recordId: issue.candidateId,
														});
													}}
												>
													Open the improvement
												</a>
											)}
											{issue.notificationId !== null && (
												<>
													{' · '}
													<a
														href="/workspace/notifications"
														onClick={(event: MouseEvent) => {
															if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
															event.preventDefault();
															navigate({ destination: 'workspace', section: 'notifications', recordId: null });
														}}
													>
														Open the notification
													</a>
												</>
											)}
										</p>
										<Technical summary="Lifecycle history">
											<ol class="small">
												{events.map((event) => (
													<li key={event.sequence}>
														<code>{event.action}</code> {event.fromState ?? '—'} → {event.toState} ·{' '}
														{when(event.at)} · {event.reason}
													</li>
												))}
											</ol>
										</Technical>
										{!resolved && (
											<div class="candidate-form">
												<label>
													Reason (optional)
													<input
														type="text"
														value={reason}
														onInput={(event) => setReason((event.target as HTMLInputElement).value)}
														placeholder="Why are you dismissing this?"
													/>
												</label>
												<button
													type="button"
													class="button ghost"
													disabled={busy !== null}
													onClick={() =>
														void act(
															`/api/scout/gateway/health/issues/${encodeURIComponent(issue.issueId)}/dismiss`,
															{ reason },
															'Dismiss',
														)
													}
												>
													{busy === 'Dismiss' ? 'Dismissing…' : 'Dismiss'}
												</button>
											</div>
										)}
										{issue.state === 'DISMISSED' && (
											<button
												type="button"
												class="button ghost small"
												disabled={busy !== null}
												onClick={() =>
													void act(
														`/api/scout/gateway/health/issues/${encodeURIComponent(issue.issueId)}/reopen`,
														{ reason: 'reopened by the Owner' },
														'Reopen',
													)
												}
											>
												{busy === 'Reopen' ? 'Reopening…' : 'Reopen'}
											</button>
										)}
									</li>
								);
							})}
						</ul>
					)}
				</>
			)}
		</section>
	);
}
